import { type POSSIBLE_METRICS_ENUM } from "constants/metrics"
import { formatters } from "@tokenterminal/core/utils/sharedFormatter"
import { type MetricConfiguration } from "@tokenterminal/tt-analytics-api-types/dist/api/metrics"
import { formatDate } from "@tokenterminal/ui/utils"
import numeral from "numeral"

import { type ProjectMetricAggregationKeys } from "types/ApiTypes"
import { capitalize } from "utils/lodash/capitalize"

const CHANGE_AGGREGATES = ["trends", "changes"] as const

type ChangeAggregate = (typeof CHANGE_AGGREGATES)[number]

const isChangeTrendOption = (aggregate: string): aggregate is ChangeAggregate =>
  CHANGE_AGGREGATES.includes(aggregate as ChangeAggregate)

const formatPercentage = (value?: number | string) => {
  if (typeof value !== "number") {
    return undefined
  }
  return numeral(value).format("0.000")
}

export const generateDownloadCSV = ({
  csv,
  filename,
}: {
  csv: string
  filename: string
}): void => {
  const link = document.createElement("a")
  link.setAttribute(
    "href",
    "data:text/csv;charset=utf-8,%EF%BB%BF" + encodeURIComponent(csv)
  )
  link.setAttribute(
    "download",
    capitalize(filename) + " " + formatDate(new Date()) + ".csv"
  )
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/*
 * --------------------------------------
 *
 * Table CSV generation is separated from the Custom chart CSV for now. Table now has types support
 */

const tableCSVDefaultKeys = ["project", "twitter_followers"]
const tableCSVKeysWithNoPrefix = [
  "tokenholders",
  "active_users",
  "active_developers",
  "code_commits",
  "address",
  "transactions",
  "gas_used",
]

type CSVColumnData = {
  metric: POSSIBLE_METRICS_ENUM | "project"
  aggregate: ProjectMetricAggregationKeys
  value?: number | string | null
  metricConfigurations: Array<MetricConfiguration>
}
// More modern and better typed
export const formatTableCSVCellByColumnData = ({
  metric,
  aggregate,
  value,
  metricConfigurations,
}: CSVColumnData) => {
  if (value == null) {
    return ""
  }
  if (tableCSVDefaultKeys.includes(metric)) {
    return value
  } else if (isChangeTrendOption(aggregate)) {
    return formatPercentage(value)
  }

  if (tableCSVKeysWithNoPrefix.includes(metric)) {
    return value
  }

  try {
    const metricConfig = metricConfigurations.find(
      ({ metric_id }) => metric_id === metric
    )!
    if (
      metricConfig &&
      metricConfig.format === "percent" &&
      typeof value === "number"
    ) {
      return formatters.number({
        value,
        format: "0.0000",
      })
    }
  } catch (e) {
    console.log("unable to find metric config for metric", metric)
  }

  return formatters.number({
    value,
    format: "0.00",
  })
}
